import React, { useEffect, useState } from 'react';
import './Dropdown.css';
import LoadingOverlay from '../components/LoadingOverlay';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';

// Helper: format number to BRL
const formatBRL = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

// Helper: convert File -> base64 (without prefix)
const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = reader.result || '';
    const str = typeof result === 'string' ? result : '';
    const base64 = (str.includes(',')) ? str.split(',')[1] : str;
    resolve(base64);
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// Upload helper similar to EntregasProdutos (multipart with fallbacks)
const apiUploadMultipart = async (endpoint, formData) => {
  const bases = [BASE_URL.replace(/\/api\/?$/, '')];
  let lastErr;
  for (const base of bases) {
    const url = `${base}/api/${endpoint}`;
    try {
      const res = await fetch(url, { method: 'POST', body: formData });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const text = await res.text();
      try { return JSON.parse(text); } catch { return text; }
    } catch (err) {
      lastErr = err;
      continue;
    }
  }
  throw lastErr || new Error('Falha ao enviar arquivos');
};

// Shared API call with multiple local fallbacks
const apiCall = async (endpoint, method = 'GET', data = null) => {
  const urls = [
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`
  ];

  for (const url of urls) {
    try {
      const config = { method, headers: { 'Content-Type': 'application/json' } };
      const token = getAuthToken();
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      if (data && (method === 'POST' || method === 'PUT')) config.body = JSON.stringify(data);
      const res = await fetch(url, config);
      if (res.ok || (res.status >= 200 && res.status < 300)) {
        const ct = res.headers.get('Content-Type') || '';
        if (ct.includes('application/json')) {
          try { return await res.json(); } catch { return { success: true, status: res.status }; }
        }
        return await res.text();
      }
    } catch (e) {
      // try next base
    }
  }
  throw new Error(`Nenhuma URL da API funcionou para ${method} ${endpoint}.`);
};

export default function ProjetoVisualDropdown() {
  const [projetos, setProjetos] = useState([]);
  const [filtradas, setFiltradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filtro
  const [filtroCampo, setFiltroCampo] = useState('cliente');
  const [filtroValor, setFiltroValor] = useState('');
  const [dropdownAberto, setDropdownAberto] = useState(false);

  // Modal detalhe
  const [detalheAberto, setDetalheAberto] = useState(false);
  const [selecionado, setSelecionado] = useState(null);

  // Modal edição
  const [editarAberto, setEditarAberto] = useState(false);
  const [projetoEditando, setProjetoEditando] = useState(null);
  // Seleção local (múltiplos)
  const [imagensSel, setImagensSel] = useState([]); // [{file, preview}]
  const [pdfsSel, setPdfsSel] = useState([]);       // [{file}]
  const [uploadingImgs, setUploadingImgs] = useState(false);
  const [uploadingPdfs, setUploadingPdfs] = useState(false);
  const [pdfInfoModal, setPdfInfoModal] = useState(null); // { name: string } | null
  // Arquivos já salvos (GET)
  const [arquivosSalvos, setArquivosSalvos] = useState({ fotos: [], pdfs: [] });
  const [loadingArquivos, setLoadingArquivos] = useState(false);
  const [arquivoActionModal, setArquivoActionModal] = useState(null); // { id, nome, tipo } | null

  useEffect(() => {
    fetchProjetos();
  }, []);

  // Dispatch popup-state so AjudaWidget shows "Resumo deste formulário" button
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('projeto-visual-popup-state', {
      detail: { formularioAberto: editarAberto }
    }));
  }, [editarAberto]);

  // Listen for close-all-popups event from AjudaWidget
  useEffect(() => {
    const handler = () => {
      setEditarAberto(false);
      setDetalheAberto(false);
      setDropdownAberto(false);
      setSelecionado(null);
      setProjetoEditando(null);
      setImagensSel([]);
      setPdfsSel([]);
      setPdfInfoModal(null);
      setArquivoActionModal(null);
    };
    window.addEventListener('projeto-visual-fechar-todos-popups', handler);
    return () => window.removeEventListener('projeto-visual-fechar-todos-popups', handler);
  }, []);

  const fetchProjetos = async () => {
    try {
      setLoading(true);
      const data = await apiCall('ProjetoVisual');
      const arr = Array.isArray(data) ? data : [];
      setProjetos(arr);
      setFiltradas(arr);
      setError('');
    } catch (e) {
      setError('Erro ao carregar projetos visuais. Verifique o backend.');
      setProjetos([]);
      setFiltradas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!filtroValor.trim()) { setFiltradas(projetos); return; }
    const val = filtroValor.toLowerCase();
    const f = projetos.filter(p => {
      if (filtroCampo === 'cliente') return (p.clienteNome || '').toLowerCase().includes(val);
      if (filtroCampo === 'descricao') return (p.descricao || '').toLowerCase().includes(val);
      if (filtroCampo === 'tipo') return (p.tipoDocumento || '').toLowerCase().includes(val);
      if (filtroCampo === 'producao') return (p.producoesId || '').toString().toLowerCase().includes(val);
      return true;
    });
    setFiltradas(f);
  }, [filtroValor, filtroCampo, projetos]);

  const abrirDetalhe = (p) => {
    setSelecionado(p);
    setDetalheAberto(true);
  };

  const abrirEditar = (p) => {
    setProjetoEditando({ ...p });
    setEditarAberto(true);
    // reset selections when opening
    try { imagensSel.forEach(i => URL.revokeObjectURL(i.preview)); } catch {}
    setImagensSel([]);
    setPdfsSel([]);
    // carregar arquivos salvos
    carregarArquivosSalvos(p.id);
  };

  const carregarArquivosSalvos = async (projetoId) => {
    setLoadingArquivos(true);
    try {
      const data = await apiCall(`ProjetoVisualArquivo/por-projeto/${projetoId}`);
      const arquivos = data?.Arquivos || data?.arquivos || [];
      const fotos = arquivos.filter(a => (a.TipoArquivo || a.tipoArquivo || '').toLowerCase() === 'foto');
      const pdfs = arquivos.filter(a => (a.TipoArquivo || a.tipoArquivo || '').toLowerCase() === 'pdf');
      setArquivosSalvos({ fotos, pdfs });
    } catch (err) {
      console.error('Erro ao carregar arquivos salvos:', err);
      setArquivosSalvos({ fotos: [], pdfs: [] });
    } finally {
      setLoadingArquivos(false);
    }
  };

  const handleSalvarEdicao = async () => {
    try {
      setLoading(true);
      await apiCall(`ProjetoVisual/${projetoEditando.id}`, 'PUT', projetoEditando);
      alert('Projeto visual atualizado com sucesso!');
      setEditarAberto(false);
      fetchProjetos();
    } catch (error) {
      alert('Erro ao atualizar projeto visual');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnviarArquivos = async () => {
    if (!projetoEditando?.id) {
      alert('Projeto visual não encontrado.');
      return;
    }
    if ((!imagensSel || imagensSel.length === 0) && (!pdfsSel || pdfsSel.length === 0)) {
      alert('Selecione pelo menos uma imagem ou PDF para enviar.');
      return;
    }
    try {
      setLoading(true);
      await uploadArquivosSelecionados(projetoEditando.id);
      alert('Arquivos enviados com sucesso!');
      // Recarregar lista de arquivos salvos
      await carregarArquivosSalvos(projetoEditando.id);
    } catch (error) {
      alert('Erro ao enviar arquivos: ' + error.message);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBaixarArquivo = async (arquivoId, nomeArquivo) => {
    try {
      const bases = [BASE_URL.replace(/\/api\/?$/, '')];
      let downloaded = false;
      for (const base of bases) {
        try {
          const url = `${base}/api/ProjetoVisualArquivo/download/${arquivoId}`;
          const res = await fetch(url);
          if (!res.ok) continue;
          const blob = await res.blob();
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = nomeArquivo || `arquivo-${arquivoId}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href);
          downloaded = true;
          break;
        } catch {}
      }
      if (!downloaded) throw new Error('Falha ao baixar arquivo');
      setArquivoActionModal(null);
    } catch (err) {
      console.error(err);
      alert('Erro ao baixar arquivo.');
    }
  };

  const handleDeletarArquivo = async (arquivoId) => {
    if (!window.confirm('Tem certeza que deseja deletar este arquivo?')) {
      setArquivoActionModal(null);
      return;
    }
    try {
      await apiCall(`ProjetoVisualArquivo/${arquivoId}`, 'DELETE');
      alert('Arquivo deletado com sucesso!');
      setArquivoActionModal(null);
      // Recarregar lista
      await carregarArquivosSalvos(projetoEditando.id);
    } catch (err) {
      console.error(err);
      alert('Erro ao deletar arquivo.');
    }
  };
  // Handle choose images (multiple)
  const onChooseImages = (e) => {
    const files = Array.from(e.target.files || []).filter(f => f.type && f.type.startsWith('image/'));
    if (!files.length) { e.target.value = ''; return; }
    setImagensSel(prev => {
      const keys = new Set(prev.map(p => `${p.file.name}_${p.file.size}`));
      const add = [];
      files.forEach(f => {
        const k = `${f.name}_${f.size}`;
        if (!keys.has(k)) add.push({ file: f, preview: URL.createObjectURL(f) });
      });
      return [...prev, ...add];
    });
    e.target.value = '';
  };

  // Handle choose PDFs (multiple)
  const onChoosePdfs = (e) => {
    const files = Array.from(e.target.files || []).filter(f => f.type === 'application/pdf');
    if (!files.length) { e.target.value = ''; return; }
    setPdfsSel(prev => {
      const keys = new Set(prev.map(p => `${p.file.name}_${p.file.size}`));
      const add = [];
      files.forEach(f => {
        const k = `${f.name}_${f.size}`;
        if (!keys.has(k)) add.push({ file: f });
      });
      return [...prev, ...add];
    });
    e.target.value = '';
  };

  // Upload helpers (try multiple endpoints)
  const uploadImagens = async () => {
    if (!projetoEditando?.id || imagensSel.length === 0) return;
    setUploadingImgs(true);
    try {
      const fd = new FormData();
      imagensSel.forEach(({ file }) => fd.append('files', file, file.name));
      fd.append('tipoArquivo', 'Foto');
      fd.append('projetoVisualId', String(projetoEditando.id));
      const tries = [
        `ProjetoVisual/${projetoEditando.id}/imagens`,
        `ProjetoVisual/${projetoEditando.id}/fotos`,
        `ProjetoVisual/${projetoEditando.id}/arquivos?tipo=Foto`,
        `ProjetoVisual/${projetoEditando.id}/upload-imagem`,
        `ProjetoVisualArquivo/${projetoEditando.id}/imagens`,
        `ProjetoVisualArquivo?projetoVisualId=${projetoEditando.id}`,
      ];
      let sent = false; let lastErr;
      for (const ep of tries) {
        try { await apiUploadMultipart(ep, fd); sent = true; break; } catch (err) { lastErr = err; }
      }
      if (!sent) throw lastErr || new Error('Falha ao enviar imagens');
      // clear previews
      try { imagensSel.forEach(i => URL.revokeObjectURL(i.preview)); } catch {}
      setImagensSel([]);
      alert('Imagens enviadas com sucesso!');
    } catch (err) {
      console.error(err);
      alert('Erro ao enviar imagens. Verifique o backend de Projeto Visual.');
    } finally {
      setUploadingImgs(false);
    }
  };

  const uploadPdfs = async () => {
    if (!projetoEditando?.id || pdfsSel.length === 0) return;
    setUploadingPdfs(true);
    try {
      const fd = new FormData();
      pdfsSel.forEach(({ file }) => fd.append('files', file, file.name));
      fd.append('tipoArquivo', 'PDF');
      fd.append('projetoVisualId', String(projetoEditando.id));
      const tries = [
        `ProjetoVisual/${projetoEditando.id}/pdfs`,
        `ProjetoVisual/${projetoEditando.id}/arquivos?tipo=PDF`,
        `ProjetoVisual/${projetoEditando.id}/upload-pdf`,
        `ProjetoVisualArquivo/${projetoEditando.id}/pdfs`,
        `ProjetoVisualArquivo?projetoVisualId=${projetoEditando.id}&tipo=PDF`,
      ];
      let sent = false; let lastErr;
      for (const ep of tries) {
        try { await apiUploadMultipart(ep, fd); sent = true; break; } catch (err) { lastErr = err; }
      }
      if (!sent) throw lastErr || new Error('Falha ao enviar PDFs');
      setPdfsSel([]);
      alert('PDFs enviados com sucesso!');
    } catch (err) {
      console.error(err);
      alert('Erro ao enviar PDFs. Verifique o backend de Projeto Visual.');
    } finally {
      setUploadingPdfs(false);
    }
  };

  const uploadTudo = async () => {
    // envia imagens e PDFs em sequência
    if (imagensSel.length) await uploadImagens();
    if (pdfsSel.length) await uploadPdfs();
  };

  // Envio unificado no Salvar Alterações -> POST para ProjetoVisualArquivo/upload
  const uploadArquivosSelecionados = async (projetoId) => {
    const endpoint = 'ProjetoVisualArquivo/upload';
    const bases = [BASE_URL.replace(/\/api\/?$/, '')];

    // Enviar todos os arquivos de uma vez (backend processa lista)
    const fd = new FormData();
    fd.append('ProjetoVisualId', String(projetoId));
    
    // Adicionar todos os arquivos na lista "Arquivos"
    imagensSel.forEach((img) => {
      fd.append('Arquivos', img.file, img.file.name);
    });
    pdfsSel.forEach((p) => {
      fd.append('Arquivos', p.file, p.file.name);
    });

    let lastErr;
    for (const base of bases) {
      try {
        const url = `${base}/api/${endpoint}`;
        const res = await fetch(url, { method: 'POST', body: fd });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`${res.status} ${res.statusText}: ${text}`);
        }
        // Sucesso - limpar seleções
        try { imagensSel.forEach(i => URL.revokeObjectURL(i.preview)); } catch {}
        setImagensSel([]);
        setPdfsSel([]);
        return; // sucesso
      } catch (err) {
        lastErr = err;
        continue;
      }
    }
    throw lastErr || new Error('Falha ao enviar arquivos para ProjetoVisualArquivo/upload');
  };

  const Campo = ({ label, value, money = false }) => (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 120 }}>
      <label style={{ fontWeight: 600, marginBottom: 4 }}>{label}</label>
      <span>{money ? formatBRL(value) : (value ?? '—')}</span>
    </div>
  );

  return (
    <div className="container-geral fundo-branco" style={{ minHeight: 'calc(100vh - 60px)', background: '#f0f0f0', padding: 20 }}>
      <h2 style={{ textAlign: 'center', color: 'rgb(235, 133, 0)', marginBottom: 20 }}>📐 Projetos Visuais</h2>

      {/* Filtro */}
      <div className="filtro-container">
        <div className="filtro-wrapper">
          <input
            type="text"
            placeholder={`Filtrar por ${
              filtroCampo === 'cliente' ? 'Cliente' :
              filtroCampo === 'descricao' ? 'Descrição' :
              filtroCampo === 'tipo' ? 'Tipo de Documento' :
              filtroCampo === 'producao' ? 'ID Projeto' : '—'
            }...`}
            value={filtroValor}
            onChange={(e) => setFiltroValor(e.target.value)}
            className="filtro-input"
          />
          <div className="dropdown-wrapper">
            <button className="dropdown-button" onClick={() => setDropdownAberto(!dropdownAberto)}>
              {filtroCampo === 'cliente' ? 'Cliente' :
               filtroCampo === 'descricao' ? 'Descrição' :
               filtroCampo === 'tipo' ? 'Tipo Documento' :
               filtroCampo === 'producao' ? 'ID Projeto' : 'Filtrar por'}
              <span className="dropdown-arrow">▼</span>
            </button>
            {dropdownAberto && (
              <div className="dropdown-menu">
                <div className="dropdown-item" onClick={() => { setFiltroCampo('cliente'); setDropdownAberto(false); }}>Cliente</div>
                <div className="dropdown-item" onClick={() => { setFiltroCampo('descricao'); setDropdownAberto(false); }}>Descrição</div>
                <div className="dropdown-item" onClick={() => { setFiltroCampo('tipo'); setDropdownAberto(false); }}>Tipo Documento</div>
                <div className="dropdown-item" onClick={() => { setFiltroCampo('producao'); setDropdownAberto(false); }}>ID Projeto</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Estados */}
      {loading && (<div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Carregando projetos visuais...</div>)}
      {error && (
        <div style={{ textAlign: 'center', padding: 20, color: '#b00020', background: '#fdecea', borderRadius: 6, margin: '20px 0' }}>{error}</div>
      )}

      {/* Lista */}
      {!loading && !error && (
        <div className="projeto-visual-cards-container" style={{ display: 'flex', flexDirection: 'column', gap: 18, marginTop: 20 }}>
          {(!filtradas || filtradas.length === 0) ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#666', background: 'white', marginTop: 10 }}>
              {filtroValor ? 'Nenhum projeto visual encontrado para o filtro' : 'Nenhum projeto visual cadastrado'}
            </div>
          ) : (
            filtradas.map((p, idx) => {
              return (
                <div className="projeto-visual-card" key={p.id || idx} style={{
                  background: 'white',
                  borderRadius: 14,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                  border: '2.5px solid rgb(235, 133, 0)',
                  padding: 32,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}>
                  <div className="projeto-visual-card-content" style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
                    <div className="projeto-visual-card-fields" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 14 }}>
                      <div className="projeto-visual-field projeto-visual-field-id">
                        <span className="projeto-visual-field-label" style={{ color: '#666', fontWeight: '600' }}>ID:</span>{' '}
                        <span className="projeto-visual-field-value">#{p.id}</span>
                      </div>

                      <div className="projeto-visual-field projeto-visual-field-producao">
                        <span className="projeto-visual-field-label" style={{ color: '#666', fontWeight: '600' }}>Projeto:</span>{' '}
                        <span className="projeto-visual-field-value">#{p.producoesId}</span>
                      </div>

                      <div className="projeto-visual-field projeto-visual-field-cliente">
                        <span className="projeto-visual-field-label" style={{ color: '#666', fontWeight: '600' }}>Cliente ID:</span>{' '}
                        <span className="projeto-visual-field-value">{p.id_cliente}</span>
                      </div>

                      <div className="projeto-visual-field projeto-visual-field-oportunidade">
                        <span className="projeto-visual-field-label" style={{ color: '#666', fontWeight: '600' }}>Oportunidade:</span>{' '}
                        <span className="projeto-visual-field-value">{p.oportunidadeId || '—'}</span>
                      </div>

                      <div className="projeto-visual-field projeto-visual-field-contrato">
                        <span className="projeto-visual-field-label" style={{ color: '#666', fontWeight: '600' }}>Contrato:</span>{' '}
                        <span className="projeto-visual-field-value">{p.contratoId || '—'}</span>
                      </div>

                      <div className="projeto-visual-field projeto-visual-field-numero">
                        <span className="projeto-visual-field-label" style={{ color: '#666', fontWeight: '600' }}>Nº Projeto:</span>{' '}
                        <span className="projeto-visual-field-value">{p.numeroProjetoExterno || '—'}</span>
                      </div>

                      <div className="projeto-visual-field projeto-visual-field-tipo" style={{ gridColumn: 'span 2' }}>
                        <span className="projeto-visual-field-label" style={{ color: '#666', fontWeight: '600' }}>Tipo Documento:</span>{' '}
                        <span className="projeto-visual-field-value" style={{ color: 'rgb(235, 133, 0)', fontWeight: '500' }}>{p.tipoDocumento || '—'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="projeto-visual-card-actions" style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-end' }}>
                    <button 
                      className="btn-detalhe-projeto-visual"
                      onClick={() => abrirDetalhe(p)}
                      style={{ 
                        background: '#2196F3', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 7, 
                        padding: '10px 24px', 
                        fontWeight: 600, 
                        fontSize: 16, 
                        minWidth: 120, 
                        cursor: 'pointer',
                        transition: 'opacity 0.2s'
                      }}
                    >
                      Ver Detalhes
                    </button>
                    <button 
                      className="btn-editar-projeto-visual"
                      onClick={() => abrirEditar(p)}
                      style={{ 
                        background: 'rgb(235, 133, 0)', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 7, 
                        padding: '10px 24px', 
                        fontWeight: 600, 
                        fontSize: 16, 
                        minWidth: 120, 
                        cursor: 'pointer',
                        transition: 'opacity 0.2s'
                      }}
                    >
                      Editar
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Modal Detalhe */}
      {detalheAberto && selecionado && (
        <div className="modal-overlay" onClick={() => setDetalheAberto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '92%', maxWidth: 900, background: '#fff', borderRadius: 12, padding: 20, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>Projeto Visual - Detalhes</h3>
              <button onClick={() => setDetalheAberto(false)} aria-label="Fechar" title="Fechar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}>×</button>
            </div>
              <div className="formulario-projeto-visual-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>ID</label>
                <input type="text" readOnly value={selecionado.id ?? ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Cliente ID</label>
                <input type="text" readOnly value={selecionado.id_cliente ?? ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Oportunidade ID</label>
                <input type="text" readOnly value={selecionado.oportunidadeId ?? ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Contrato ID</label>
                <input type="text" readOnly value={selecionado.contratoId ?? ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Projeto ID</label>
                <input type="text" readOnly value={selecionado.producoesId ?? ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Tipo Documento</label>
                <input type="text" readOnly value={selecionado.tipoDocumento ?? ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Número Projeto Externo</label>
                <input type="text" readOnly value={selecionado.numeroProjetoExterno ?? ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white' }} />
              </div>
              {/* Removido botão 'Abrir arquivo'. Caso necessário, exibimos dados básicos somente. */}
              <div style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 3' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Descrição</label>
                <textarea readOnly value={selecionado.descricao ?? ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white', minHeight: 80, fontFamily: 'inherit', resize: 'vertical' }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar */}
      {editarAberto && projetoEditando && (
        <div className="modal-overlay" onClick={() => setEditarAberto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div id="projeto-visual-form" className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '92%', maxWidth: 900, background: '#fff', borderRadius: 12, padding: 20, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>Editar Projeto Visual</h3>
              <button onClick={() => setEditarAberto(false)} aria-label="Fechar" title="Fechar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer' }}>×</button>
            </div>
            
            {/* Campos bloqueados */}
            <div id="projeto-visual-secao-refs" style={{ marginBottom: 20 }}>
              <div className="formulario-projeto-visual-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>ID</label>
                  <input
                    type="text"
                    value={projetoEditando.id || ''}
                    readOnly
                    style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Cliente ID</label>
                  <input
                    type="text"
                    value={projetoEditando.id_cliente || ''}
                    readOnly
                    style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Oportunidade ID</label>
                  <input
                    type="text"
                    value={projetoEditando.oportunidadeId || ''}
                    readOnly
                    style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Contrato ID</label>
                  <input
                    type="text"
                    value={projetoEditando.contratoId || ''}
                    readOnly
                    style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Projeto ID</label>
                  <input
                    type="text"
                    value={projetoEditando.producoesId || ''}
                    readOnly
                    style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
              </div>
            </div>

            {/* Campos editáveis */}
            <div id="projeto-visual-secao-dados">
              <div className="formulario-projeto-visual-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 2' }}>
                  <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Descrição</label>
                  <textarea
                    value={projetoEditando.descricao || ''}
                    onChange={(e) => setProjetoEditando({ ...projetoEditando, descricao: e.target.value })}
                    style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', minHeight: 60, fontFamily: 'inherit' }}
                    placeholder="Descreva o projeto visual"
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Tipo Documento</label>
                  <select
                    value={projetoEditando.tipoDocumento || ''}
                    onChange={(e) => setProjetoEditando({ ...projetoEditando, tipoDocumento: e.target.value })}
                    style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', cursor: 'pointer' }}
                  >
                    <option value="">Selecione</option>
                    <option value="DWG">DWG (AutoCAD)</option>
                    <option value="PDF">PDF</option>
                    <option value="PNG">PNG (Imagem)</option>
                    <option value="JPG">JPG (Imagem)</option>
                    <option value="SKP">SKP (SketchUp)</option>
                    <option value="RVT">RVT (Revit)</option>
                    <option value="OUTROS">OUTROS</option>
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Número Projeto Externo</label>
                  <input
                    type="text"
                    value={projetoEditando.numeroProjetoExterno || ''}
                    onChange={(e) => setProjetoEditando({ ...projetoEditando, numeroProjetoExterno: e.target.value })}
                    style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }}
                    placeholder="Ex: CAD-2025-001"
                  />
                </div>
                <div id="projeto-visual-secao-uploads" style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 2', gap: 10 }}>
                  {/* Seção Imagens (múltiplas) */}
                  <div style={{ background: '#fafafa', border: '1px dashed rgb(235, 133, 0)', borderRadius: 8, padding: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 700, color: 'rgb(235, 133, 0)' }}>Imagens do Projeto</div>
                        <div style={{ fontSize: 12, color: '#666' }}>PNG, JPG... (múltiplas)</div>
                      </div>
                      <label className="botao-editar" style={{ backgroundColor: 'rgb(235, 133, 0)', cursor: 'pointer' }}>
                        Escolher imagens
                        <input type="file" accept="image/*" multiple onChange={onChooseImages} style={{ display: 'none' }} />
                      </label>
                    </div>
                    {imagensSel.length > 0 && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                        {imagensSel.map((p, idx) => (
                          <div key={idx} style={{ position: 'relative', width: 90, height: 90, borderRadius: 8, overflow: 'hidden', border: '1px solid #ddd' }}>
                            <img src={p.preview} alt={p.file.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            <button type="button" aria-label={`Remover imagem ${idx+1}`} onClick={() => {
                              setImagensSel(prev => {
                                const copy = [...prev];
                                try { URL.revokeObjectURL(copy[idx].preview); } catch {}
                                copy.splice(idx,1);
                                return copy;
                              });
                            }} style={{ position: 'absolute', top: 6, right: 6, background: '#d32f2f', color: '#fff', border: 'none', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, lineHeight: '14px', cursor: 'pointer', padding: 0 }}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Removido botão 'Enviar Imagens' (upload no Salvar Alterações) */}
                  </div>

                  {/* Seção PDFs (múltiplos) */}
                  <div style={{ background: '#fafafa', border: '1px dashed #2196F3', borderRadius: 8, padding: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 700, color: '#2196F3' }}>Documentos PDF</div>
                        <div style={{ fontSize: 12, color: '#666' }}>Somente PDF (múltiplos)</div>
                      </div>
                      <label className="botao-editar" style={{ backgroundColor: '#2196F3', cursor: 'pointer' }}>
                        Escolher PDFs
                        <input type="file" accept="application/pdf" multiple onChange={onChoosePdfs} style={{ display: 'none' }} />
                      </label>
                    </div>
                    {pdfsSel.length > 0 && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                        {pdfsSel.map((p, idx) => (
                          <div key={idx} style={{ position: 'relative', width: 90, height: 90, borderRadius: 8, background: '#fff', border: '1px solid #ddd', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36 }}>
                              <span role="img" aria-label="pdf" onClick={() => setPdfInfoModal({ name: p.file.name })} style={{ cursor: 'pointer' }}>📄</span>
                            </div>
                            <button type="button" title={p.file.name} onClick={() => setPdfInfoModal({ name: p.file.name })} style={{ width: '100%', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10, color: '#1565C0', background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 4px', lineHeight: 1.2 }}>
                              {p.file.name}
                            </button>
                            <button type="button" aria-label={`Remover PDF ${idx+1}`} onClick={() => {
                              setPdfsSel(prev => {
                                const copy = [...prev];
                                copy.splice(idx,1);
                                return copy;
                              });
                            }} style={{ position: 'absolute', top: 6, right: 6, background: '#d32f2f', color: '#fff', border: 'none', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, lineHeight: '14px', cursor: 'pointer', padding: 0 }}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Removidos botões de envio direto; upload ocorre ao salvar alterações */}
                  </div>

                  {/* Enviar tudo (imagens+PDFs) */}
                  {/* Removido botão 'Enviar Tudo' (upload no Salvar Alterações) */}
                </div>

                {/* Seção de arquivos já salvos (GET) */}
                <div id="projeto-visual-secao-arquivos-salvos" style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 2', marginTop: 20, gap: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#333', marginBottom: 6 }}>Arquivos já enviados</div>
                  {loadingArquivos && <div style={{ fontSize: 13, color: '#999' }}>Carregando arquivos...</div>}
                  {!loadingArquivos && arquivosSalvos.fotos.length === 0 && arquivosSalvos.pdfs.length === 0 && (
                    <div style={{ fontSize: 13, color: '#999' }}>Nenhum arquivo enviado ainda.</div>
                  )}

                  {/* Container invisível de imagens salvas */}
                  {arquivosSalvos.fotos.length > 0 && (
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#666', marginBottom: 6 }}>Imagens</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {arquivosSalvos.fotos.map((foto) => {
                          const fotoId = foto.Id || foto.id;
                          const nomeArquivo = foto.NomeArquivo || foto.nomeArquivo || `Foto ${fotoId}`;
                          return (
                            <div
                              key={fotoId}
                              onClick={() => setArquivoActionModal({ id: fotoId, nome: nomeArquivo, tipo: 'foto' })}
                              style={{ position: 'relative', width: 90, height: 90, borderRadius: 8, overflow: 'hidden', border: '1px solid #ddd', cursor: 'pointer', background: '#f9f9f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              <img src={`${BASE_URL}/ProjetoVisualArquivo/download/${fotoId}`} alt={nomeArquivo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div style="color:#999;font-size:11px;text-align:center;">Sem preview</div>'; }} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Container invisível de PDFs salvos */}
                  {arquivosSalvos.pdfs.length > 0 && (
                    <div style={{ marginTop: arquivosSalvos.fotos.length > 0 ? 10 : 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#666', marginBottom: 6 }}>PDFs</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {arquivosSalvos.pdfs.map((pdf) => {
                          const pdfId = pdf.Id || pdf.id;
                          const nomeArquivo = pdf.NomeArquivo || pdf.nomeArquivo || `PDF ${pdfId}`;
                          return (
                            <div
                              key={pdfId}
                              onClick={() => setArquivoActionModal({ id: pdfId, nome: nomeArquivo, tipo: 'pdf' })}
                              style={{ position: 'relative', width: 90, height: 90, borderRadius: 8, background: '#fff', border: '1px solid #ddd', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                            >
                              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36 }}>
                                <span role="img" aria-label="pdf">📄</span>
                              </div>
                              <button type="button" title={nomeArquivo} style={{ width: '100%', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10, color: '#1565C0', background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 4px', lineHeight: 1.2, pointerEvents: 'none' }}>
                                {nomeArquivo}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal de informação de PDF (arquivo selecionado) */}
            {pdfInfoModal && (
              <div className="modal-overlay" onClick={() => setPdfInfoModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
                <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '92%', maxWidth: 420, background: '#fff', borderRadius: 12, padding: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h3 style={{ margin: 0, color: '#2196F3', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span role="img" aria-label="pdf">📄</span>
                      Arquivo PDF
                    </h3>
                    <button onClick={() => setPdfInfoModal(null)} aria-label="Fechar" title="Fechar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer' }}>×</button>
                  </div>
                  <div style={{ fontSize: 14, color: '#333', wordBreak: 'break-word' }}>
                    {pdfInfoModal.name}
                  </div>
                </div>
              </div>
            )}

            {/* Modal de ação de arquivo salvo (Baixar/Deletar) */}
            {arquivoActionModal && (
              <div className="modal-overlay" onClick={() => setArquivoActionModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
                <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '92%', maxWidth: 420, background: '#fff', borderRadius: 12, padding: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)', display: 'flex', alignItems: 'center', gap: 8 }}>
                      {arquivoActionModal.tipo === 'foto' ? '🖼️' : '📄'} {arquivoActionModal.nome}
                    </h3>
                    <button onClick={() => setArquivoActionModal(null)} aria-label="Fechar" title="Fechar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer' }}>×</button>
                  </div>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 20 }}>
                    <button
                      onClick={() => handleBaixarArquivo(arquivoActionModal.id, arquivoActionModal.nome)}
                      style={{ padding: '12px 24px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 15 }}
                    >
                      BAIXAR ARQUIVO
                    </button>
                    <button
                      onClick={() => handleDeletarArquivo(arquivoActionModal.id)}
                      style={{ padding: '12px 24px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 15 }}
                    >
                      DELETAR ARQUIVO
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Botões */}
            <div style={{ marginTop: 25, display: 'flex', gap: 10, justifyContent: 'center', borderTop: '1px solid #ddd', paddingTop: 20 }}>
              <button
                onClick={handleSalvarEdicao}
                style={{ 
                  padding: '12px 40px', 
                  backgroundColor: 'rgb(235, 133, 0)', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: 6, 
                  cursor: 'pointer', 
                  fontWeight: 600,
                  fontSize: 15,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(208, 116, 0)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(235, 133, 0)'}
              >
                Salvar Alterações
              </button>
              <button
                onClick={handleEnviarArquivos}
                disabled={(imagensSel.length + pdfsSel.length) === 0}
                style={{ 
                  padding: '12px 40px', 
                  backgroundColor: (imagensSel.length + pdfsSel.length) === 0 ? '#ccc' : 'rgb(235, 133, 0)', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: 6, 
                  cursor: (imagensSel.length + pdfsSel.length) === 0 ? 'not-allowed' : 'pointer', 
                  fontWeight: 600,
                  fontSize: 15,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => { if ((imagensSel.length + pdfsSel.length) > 0) e.target.style.backgroundColor = 'rgb(208, 116, 0)'; }}
                onMouseLeave={(e) => { if ((imagensSel.length + pdfsSel.length) > 0) e.target.style.backgroundColor = 'rgb(235, 133, 0)'; }}
              >
                Enviar Arquivos
              </button>
              <button
                onClick={() => setEditarAberto(false)}
                style={{ 
                  padding: '12px 40px', 
                  backgroundColor: '#f44336', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: 6, 
                  cursor: 'pointer', 
                  fontWeight: 600,
                  fontSize: 15,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#da190b'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <LoadingOverlay isLoading={loading} message="Processando..." />
    </div>
  );
}
